import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { authService, type SetupTokenInfo } from '@/services/auth.service';
import { useErrorStore } from '@/stores/errorStore';
import { usePasswordPolicy } from '@/hooks/usePasswordPolicy';
import VersionBadge from '@/components/VersionBadge';
import { passwordProblem, passwordRuleText } from '@/utils/accountRules';
import { useFieldErrors, type FieldErrors } from '@/utils/formErrors';

// QA #68: the backend's rule refusals belong to the 新密碼 field.
const SET_PASSWORD_FIELDS: Record<string, string> = {
  PASSWORD_TOO_SHORT: 'new_password',
  PASSWORD_TOO_SIMPLE: 'new_password',
  PASSWORD_CONTAINS_USERNAME: 'new_password',
  PASSWORD_SAME_AS_OLD: 'new_password',
};

/**
 * QA #75: the public 設定密碼 page the invite / 寄送設定密碼信 mails link to
 * (/set-password?token=…). No administrator ever types a password for
 * someone else: the user verifies the link, picks a password here and then
 * logs in — must_change_password is cleared server-side, so the login lands
 * on 總覽 directly.
 */
type Stage = 'verifying' | 'invalid' | 'form';

const INVALID_HINT = '請聯絡管理員重新寄送設定密碼信。';

export default function SetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const showSuccess = useErrorStore((s) => s.showSuccess);
  // QA #63: the minimum length the backend enforces (GET /auth/config).
  const policy = usePasswordPolicy();

  const [stage, setStage] = useState<Stage>('verifying');
  const [info, setInfo] = useState<SetupTokenInfo | null>(null);
  const [invalidMessage, setInvalidMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // `error` is only for problems that belong to no field; field problems are
  // marked on the input (QA #68).
  const [error, setError] = useState('');
  const form = useFieldErrors();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setInvalidMessage('這個設定密碼連結不完整。');
      setStage('invalid');
      return;
    }
    authService
      .verifySetupToken(token)
      .then((data) => {
        if (cancelled) return;
        setInfo(data);
        setStage('form');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setInvalidMessage((err as Error)?.message || '這個設定密碼連結無效或已過期。');
        setStage('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    // Same rule as the backend (QA #63), marked on the field (QA #68).
    const problems: FieldErrors = {};
    const problem = passwordProblem(newPassword, policy, info?.username);
    if (problem) problems.new_password = problem;
    if (newPassword !== confirmPassword) {
      problems.confirm_password = '兩次輸入的密碼不一致，請再輸入一次相同的新密碼';
    }
    if (Object.keys(problems).length > 0) {
      form.setErrors(problems);
      return;
    }
    form.clear();
    setSubmitting(true);
    try {
      await authService.completePasswordSetup(token, newPassword);
      showSuccess('密碼已設定，請使用新密碼登入');
      navigate('/login', { replace: true });
    } catch (err) {
      const apiErr = err as { code?: string; message?: string };
      if (apiErr.code === 'SETUP_TOKEN_INVALID' || apiErr.code === 'SETUP_TOKEN_EXPIRED') {
        // The link died between verify and submit (used elsewhere / expired).
        setInvalidMessage(apiErr.message || '這個設定密碼連結無效或已過期。');
        setStage('invalid');
      } else {
        // PASSWORD_* carry the rule in Chinese and land under 新密碼.
        setError(form.apply(err, SET_PASSWORD_FIELDS, '設定密碼失敗，請稍後再試'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmMismatch = confirmPassword !== '' && confirmPassword !== newPassword;

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <LockOutlined sx={{ color: 'white' }} />
          </Box>

          <Typography component="h1" variant="h5" gutterBottom>
            設定密碼
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {import.meta.env.VITE_APP_NAME || '備份系統'}
          </Typography>

          {stage === 'verifying' && (
            <Box sx={{ py: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {stage === 'invalid' && (
            <>
              <Alert severity="error" sx={{ width: '100%', mb: 2 }} data-testid="setup-token-invalid">
                {invalidMessage} {INVALID_HINT}
              </Alert>
              <Link component={RouterLink} to="/login" variant="body2" underline="hover">
                返回登入
              </Link>
            </>
          )}

          {stage === 'form' && info && (
            <>
              <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
                您正在為帳號「{info.username}」設定密碼。
                {info.kind === 'invite' ? '設定完成後即可用新密碼登入。' : '設定完成後，其他裝置上的登入都會失效。'}
              </Alert>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* noValidate: the form's own Chinese hints replace the browser's required bubble (QA #68) */}
              <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  fullWidth
                  id="new-password"
                  label="新密碼"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  required
                  disabled={submitting}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  {...form.bind('new_password', passwordRuleText(policy))}
                />
                <TextField
                  margin="normal"
                  fullWidth
                  id="confirm-password"
                  label="確認新密碼"
                  type="password"
                  autoComplete="new-password"
                  required
                  disabled={submitting}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  {...form.bind('confirm_password', confirmMismatch ? '兩次輸入的密碼不一致' : ' ')}
                  error={confirmMismatch || Boolean(form.errors.confirm_password)}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 2 }}
                  disabled={submitting}
                >
                  {submitting ? <CircularProgress size={24} /> : '設定密碼'}
                </Button>
              </Box>
            </>
          )}
        </Paper>

        <VersionBadge align="center" sx={{ mt: 2 }} />
      </Box>
    </Container>
  );
}
